import React, { useState, useEffect, useRef } from 'react';
import { Check, CheckCheck, ArrowLeft, Phone, Video, MoreVertical } from 'lucide-react';
import { DemoTranslations } from './demoTranslations';

interface Message {
  id: string;
  text: string;
  type: 'sent' | 'received';
  time: string;
  status?: 'sent' | 'delivered' | 'read';
}

interface WhatsAppSimulatorProps {
  messages: Array<{
    text: string;
    type: 'sent' | 'received';
    delay: number; // ms antes de aparecer
    typingDuration?: number; // ms para digitar (só para sent)
  }>;
  contactName?: string;
  contactStatus?: string;
  onComplete?: () => void;
  autoStart?: boolean;
  translations: DemoTranslations;
}

export const WhatsAppSimulator: React.FC<WhatsAppSimulatorProps> = ({
  messages: messageScript,
  contactName = 'KeepNota',
  contactStatus,
  onComplete,
  autoStart = true,
  translations: t,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [typingText, setTypingText] = useState('');
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, typingText, showTypingIndicator]);

  // Auto start
  useEffect(() => {
    if (autoStart && !started) {
      const timer = setTimeout(() => {
        setStarted(true);
        runScript();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [autoStart, started]);

  const getTime = () => {
    const now = new Date();
    return now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const typeMessage = async (text: string, duration: number): Promise<void> => {
    setIsTyping(true);
    const charDelay = duration / text.length;

    for (let i = 0; i <= text.length; i++) {
      setTypingText(text.substring(0, i));
      await new Promise((resolve) => setTimeout(resolve, charDelay));
    }

    setIsTyping(false);
    setTypingText('');
  };

  const runScript = async () => {
    for (let i = 0; i < messageScript.length; i++) {
      const msg = messageScript[i];

      // Wait delay before showing
      await new Promise((resolve) => setTimeout(resolve, msg.delay));

      if (msg.type === 'sent') {
        // Type the message
        if (msg.typingDuration) {
          await typeMessage(msg.text, msg.typingDuration);
        }

        // Add to messages
        setMessages((prev) => [
          ...prev,
          {
            id: `msg-${i}`,
            text: msg.text,
            type: 'sent',
            time: getTime(),
            status: 'sent',
          },
        ]);

        // Animate status changes
        await new Promise((resolve) => setTimeout(resolve, 300));
        setMessages((prev) =>
          prev.map((m) => (m.id === `msg-${i}` ? { ...m, status: 'delivered' } : m))
        );
        await new Promise((resolve) => setTimeout(resolve, 500));
        setMessages((prev) =>
          prev.map((m) => (m.id === `msg-${i}` ? { ...m, status: 'read' } : m))
        );
      } else {
        // Show typing indicator for received messages
        setShowTypingIndicator(true);
        await new Promise((resolve) => setTimeout(resolve, 1500));
        setShowTypingIndicator(false);

        // Add received message
        setMessages((prev) => [
          ...prev,
          {
            id: `msg-${i}`,
            text: msg.text,
            type: 'received',
            time: getTime(),
          },
        ]);
      }
    }

    // Call onComplete after a short delay
    if (onComplete) {
      setTimeout(onComplete, 1500);
    }
  };

  const renderStatus = (status?: string) => {
    switch (status) {
      case 'sent':
        return <Check className="w-4 h-4 text-gray-400" />;
      case 'delivered':
        return <CheckCheck className="w-4 h-4 text-gray-400" />;
      case 'read':
        return <CheckCheck className="w-4 h-4 text-blue-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="w-full max-w-[375px] h-[812px] bg-[#111b21] rounded-[40px] overflow-hidden shadow-2xl border-[8px] border-gray-800 relative">
      {/* iPhone notch */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[150px] h-[30px] bg-gray-800 rounded-b-[20px] z-20" />

      {/* WhatsApp Header */}
      <div className="bg-[#202c33] pt-10 pb-2 px-4 flex items-center gap-3">
        <ArrowLeft className="w-6 h-6 text-gray-400" />
        <div className="w-10 h-10 rounded-full bg-[#00a884] flex items-center justify-center">
          <span className="text-white font-bold text-lg">K</span>
        </div>
        <div className="flex-1">
          <p className="text-white font-medium">{contactName}</p>
          <p className="text-[#8696a0] text-sm">{contactStatus || t.whatsapp.online}</p>
        </div>
        <Video className="w-6 h-6 text-gray-400 mx-2" />
        <Phone className="w-6 h-6 text-gray-400 mx-2" />
        <MoreVertical className="w-6 h-6 text-gray-400" />
      </div>

      {/* Chat Background */}
      <div
        className="h-[calc(100%-180px)] overflow-y-auto p-4 space-y-2"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23182229' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundColor: '#0b141a',
        }}
      >
        {/* Date badge */}
        <div className="flex justify-center mb-4">
          <span className="bg-[#182229] text-[#8696a0] text-xs px-3 py-1 rounded-lg">
            {t.whatsapp.today}
          </span>
        </div>

        {/* Messages */}
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.type === 'sent' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg px-3 py-2 ${
                message.type === 'sent'
                  ? 'bg-[#005c4b] text-white rounded-tr-none'
                  : 'bg-[#202c33] text-white rounded-tl-none'
              }`}
            >
              <p className="text-[15px] leading-relaxed">{message.text}</p>
              <div className="flex items-center justify-end gap-1 mt-1">
                <span className="text-[11px] text-[#8696a0]">{message.time}</span>
                {message.type === 'sent' && renderStatus(message.status)}
              </div>
            </div>
          </div>
        ))}

        {/* Typing indicator for received messages */}
        {showTypingIndicator && (
          <div className="flex justify-start">
            <div className="bg-[#202c33] rounded-lg px-4 py-3 rounded-tl-none">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-[#8696a0] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-[#8696a0] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-[#8696a0] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#202c33] p-3 pb-8 flex items-center gap-2">
        <div className="flex-1 bg-[#2a3942] rounded-full px-4 py-2 flex items-center">
          {isTyping ? (
            <span className="text-white text-[15px]">{typingText}</span>
          ) : (
            <span className="text-[#8696a0] text-[15px]">{t.whatsapp.message}</span>
          )}
        </div>
        <div className="w-12 h-12 bg-[#00a884] rounded-full flex items-center justify-center">
          <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
            <path d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSimulator;
